import React, {useEffect, useState, useRef} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {ActionSheet, Button, Dialog, ErrorBlock, Image, NavBar, Popup, Skeleton, Toast} from 'antd-mobile';
import { CheckOutline } from 'antd-mobile-icons';

import {useQuery} from "@apollo/client";
import {client} from "graphql/client";
import photoService, {PhotoType, PositionType } from 'pages/delivery/services/PhotoService';
import { useLocationStore } from 'store/locationStore';
import { ExceptionDictionaryType, ExceptionSubReasonType } from 'pages/delivery/graphql/queries/getExceptionDictionaryForDelivery';
import { NativePermissions } from 'pages/delivery/components/PhotoNativeBridge';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from 'utils/deviceUtils';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import {
    GET_DSP_PACKAGE_DETAIL,
    GetDspPackageDetailByTrackingNumberResponse,
    GetDspPackageDetailByTrackingNumberVariables
} from "../graphql/queries/getPackageDetail";
import {DspPath} from "../../../enums/DeliveryNavigatePathEnum";
import {uploadPhotosWithTimeout} from "../../../services/DeliveryDataService";
import {DeliveryFailedEventInput, handleDspFailed} from 'pages/delivery/graphql/mutations/deliveredFailed';
import {InfoRow} from "../../../components/InfoRow";
import {formatStopNumber} from "../../../utils/stopNumberUtils";
import {formatTimestampToSecondsTime, timestampToDate} from "../../../utils/dateTimeUtils";
import {getExceptionTypeText, getLocalizedText} from "../../../utils/i18nUtils";
import PhotoUploadCard from "../../delivery/components/PhotoUploadCard";
import PhotoViewer from "../../delivery/components/PhotoViewer";
import {getExceptionDictionaryAll, getExceptionDictionaryForDsp} from '../../delivery/graphql/queries/getExceptionDictionaryForDsp';
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import {getOrderNoteContent} from '../../../utils/orderNoteUtils';

interface DspParticularsMarkFailProps {
}

const DspParticularsMarkFailPage: React.FC<DspParticularsMarkFailProps> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { i18n } = useTranslation();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [exceptionActionSheetVisible, setExceptionActionSheetVisible] = useState<boolean>(false);
    const [exceptionDictionaries, setExceptionDictionaries] = useState<ExceptionDictionaryType[]>([]);
    const [dspExceptionDictionaries, setDspExceptionDictionaries] = useState<ExceptionDictionaryType[]>([]);
    const [exceptionReason, setExceptionReason] = useState<ExceptionDictionaryType | null>(null);
    const [exceptionSubReasons, setExceptionSubReasons] = useState<ExceptionSubReasonType[]>([]);
    const [selectedExceptionForSubReasons, setSelectedExceptionForSubReasons] = useState<ExceptionDictionaryType | null>(null);
    const [selectedSubReasonCodes, setSelectedSubReasonCodes] = useState<Set<string>>(new Set());
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const labelPhotoRef = useRef<HTMLDivElement>(null);
    const locationPhotoRef = useRef<HTMLDivElement>(null);
    const {position} = useLocationStore();
    const {trackingNumber} = useParams<{ trackingNumber: string }>();

    // 查询DSP包裹详情
    const {
        data: packageData,
        loading,
        error,
        refetch
    } = useQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL,
        {
            variables: {
                trackingNumber: trackingNumber || ""
            },
            skip: !trackingNumber,
            fetchPolicy: 'cache-and-network'
        }
    );
    const packageDetail = packageData?.getDspPackageDetailByTrackingNumber;


    useEffect(() => {
        if (!packageDetail) return;
        const podImages = packageDetail?.exceptionImageS3UrlsJson || [];
        const podImageKeys = packageDetail?.exceptionImageS3KeysJson || [];
        if (podImages.length > 0 && podImageKeys.length > 0){
            setPhotos({
                label: {
                    url: podImages[0],
                    objectKey: podImageKeys[0]
                },
                location: {
                    url: podImages[1],
                    objectKey: podImageKeys[1]
                },
                houseNumber: undefined
            });
        }
    }, [packageDetail]);

    // 计算是否在6小时内可编辑
    const failAt = packageDetail?.failAt;
    // 如果还没有标记失败，允许编辑；如果已标记失败，则判断是否在6小时内
    const canEdit = failAt ? (timestampToDate(Number(failAt) + 6 * 60 * 60) > timestampToDate(Date.now())) : true;

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 加载所有异常字典（用于异常原因查询回显）
    useEffect(() => {
        const loadExceptionDictionary = async () => {
            try {
                const dictionaries = await getExceptionDictionaryAll();
                setExceptionDictionaries(dictionaries);
            } catch (error) {
                console.error('[DspParticularsMarkFailPage] Failed to load exception dictionary:', error);
            }
        };
        loadExceptionDictionary();
    }, []);

    // 加载DSP专用异常字典（用于修改失败凭证功能）
    useEffect(() => {
        const loadDspExceptionDictionary = async () => {
            try {
                const dictionaries = await getExceptionDictionaryForDsp();
                setDspExceptionDictionaries(dictionaries);
            } catch (error) {
                console.error('[DspParticularsMarkFailPage] Failed to load DSP exception dictionary:', error);
            }
        };
        loadDspExceptionDictionary();
    }, []);

    // 从 packageDetail.exceptionReasonCode 查找异常原因字典项
    useEffect(() => {
        if (!packageDetail?.exceptionReasonCode || exceptionDictionaries.length === 0) {
            // 如果没有异常原因代码，清空 exceptionReason
            if (!packageDetail?.exceptionReasonCode) {
                setExceptionReason(null);
            }
            return;
        }

        // 如果当前 exceptionReason 的代码与 packageDetail 中的代码一致，则不需要更新
        const currentExceptionReasonCode = exceptionReason?.exceptionReasonCode;
        if (currentExceptionReasonCode === packageDetail.exceptionReasonCode) {
            return;
        }

        const foundReason = exceptionDictionaries.find(
            dict => dict.exceptionReasonCode === packageDetail.exceptionReasonCode
        );

        if (foundReason) {
            setExceptionReason(foundReason);
            if (packageDetail.exceptionSubReasons?.length && foundReason.exceptionSubReasons?.length) {
                const subReasons = packageDetail.exceptionSubReasons
                    .map(code => foundReason.exceptionSubReasons!.find(s => s.code === code))
                    .filter((s): s is ExceptionSubReasonType => !!s);
                setExceptionSubReasons(subReasons);
            } else {
                setExceptionSubReasons([]);
            }
        } else {
            // 如果找不到字典项，清空 exceptionReason，让页面显示原始代码
            setExceptionReason(null);
            setExceptionSubReasons([]);
        }
    }, [packageDetail?.exceptionReasonCode, packageDetail?.exceptionSubReasons, exceptionDictionaries]);

    // 控制删除按钮的显示/隐藏
    useEffect(() => {
        const hideDeleteButtons = (container: HTMLDivElement | null) => {
            if (!container) return;
            // 查找所有同时具有 absolute、top-2 和 right-2 类的元素
            const allElements = container.querySelectorAll('*');
            allElements.forEach(element => {
                const classList = element.classList;
                if (classList.contains('absolute') && classList.contains('top-2') && classList.contains('right-2')) {
                    (element as HTMLElement).style.display = isEditing ? '' : 'none';
                }
            });
        };

        // 使用 setTimeout 确保 DOM 已渲染
        const timer = setTimeout(() => {
            hideDeleteButtons(labelPhotoRef.current);
            hideDeleteButtons(locationPhotoRef.current);
        }, 0);

        return () => clearTimeout(timer);
    }, [isEditing, photos.label?.url, photos.location?.url]);

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!trackingNumber || error || !packageDetail) {
        return (
            <div className="p-4">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <ErrorBlock
                    status="empty"
                    title={t('pages.dsp.packageDetail.packageNotFound', '包裹不存在')}
                    description={t('pages.dsp.packageDetail.packageNotFoundDescription', '未找到该运单号对应的包裹信息')}
                />
            </div>
        );
    }

    // 处理异常原因选择点击（无二级原因时直接选中；有二级原因时弹出二级弹窗）
    const handleExceptionActionClick = (action: ExceptionDictionaryType) => {
        setExceptionActionSheetVisible(false);
        const subReasons = action.exceptionSubReasons;
        if (subReasons && subReasons.length > 0) {
            setSelectedExceptionForSubReasons(action);
            setSelectedSubReasonCodes(new Set());
        } else {
            setExceptionReason(action);
            setExceptionSubReasons([]);
        }
    };

    // 二级原因弹窗确认
    const handleSubReasonConfirm = (action: ExceptionDictionaryType, subReasons: ExceptionSubReasonType[]) => {
        setSelectedExceptionForSubReasons(null);
        setSelectedSubReasonCodes(new Set());
        setExceptionReason(action);
        setExceptionSubReasons(subReasons);
    };

    // 处理修改失败凭证按钮点击
    const handleModifyFailProof = () => {
        if (dspExceptionDictionaries.length > 0) {
            setExceptionActionSheetVisible(true);
            setIsEditing(true);
        }
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PositionType, file: File) => {
        try {
            setIsLoading(true);
            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.exception.deletePhotoTitle'),
            confirmText: t('pages.delivery.exception.deletePhotoDelete'),
            cancelText: t('pages.delivery.exception.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 标记异常
    const handleCompleteException = async () => {
        setIsLoading(true);

        try {
            if (!exceptionReason) {
                Toast.show({content: t('pages.delivery.exception.noExceptionReason', '缺少异常信息'), position: 'center'});
                setIsLoading(false);
                return;
            }

            const hasLabel = photos.label?.objectKey;
            const hasLocation = photos.location?.objectKey;
            if (exceptionReason.isUploadsImages && (!hasLabel || !hasLocation)) {
                Toast.show({content: t('pages.delivery.exception.pleaseAddExceptionPhoto'), position: 'center'});
                setIsLoading(false);
                return;
            }

            // 获取需要上传的照片
            const photosToUpload = Object.values(photos).filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined);
            // 执行照片上传
            const photoKeys = await uploadPhotosWithTimeout(photosToUpload, trackingNumber);

            // 确保 exceptionReasonCode 存在
            if (!exceptionReason?.exceptionReasonCode) {
                Toast.show({content: t('pages.delivery.exception.noExceptionReason', '缺少异常信息'), position: 'center'});
                setIsLoading(false);
                return;
            }

            // 获取原来的异常原因和新的异常原因
            const originalExceptionReasonCode = packageDetail?.exceptionReasonCode || t('common.None', '无');
            const newExceptionReasonCode = exceptionReason.exceptionReasonCode;
            const newExceptionReasonText = getExceptionTypeText(exceptionReason);

            const failedInput : DeliveryFailedEventInput = {
                trackingNumber: trackingNumber,
                location: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                },
                exceptionReasonCode: newExceptionReasonCode,
                id: packageDetail?.deliveryTaskOrder?.id || 0,
                exceptionImageS3KeysJson: photoKeys,
                ...(exceptionSubReasons.length > 0 ? { exceptionSubReasons: exceptionSubReasons.map(s => s.code) } : {})
            }
            const result = await handleDspFailed(failedInput);

            Toast.show({ content: t('pages.delivery.exception.markAsFailed'), position: 'center' });
            
            // 检查跳转来源并返回到相应页面
            const driverId = searchParams.get('driverId');
            const date = searchParams.get('date');
            const tabType = searchParams.get('tabType');
            const taskTabType = searchParams.get('taskTabType');
            
            if (taskTabType) {
                // 从任务详情页面跳转过来，返回到任务详情页面
                const taskId = searchParams.get('taskId');
                // 返回到任务详情页面，并传递tab状态
                navigate(DspPath.DELIVERY_DSP_DELIVERY_TASK_DETAILS, {
                    state: {
                        taskId: taskId ? parseInt(taskId) : 0,
                        activeTab: taskTabType
                    }
                });
            } else if (driverId && tabType) {
                // 从运营数据页面跳转过来，返回到对应的tab
                const returnParams = new URLSearchParams();
                returnParams.set('tabType', tabType);
                if (date) returnParams.set('date', date);
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS.replace(':driverId', driverId)}?${returnParams.toString()}`);
            } else {
                navigate(DspPath.DELIVERY_DSP_OPERATIONAL);
            }

        } catch (error: any) {
            console.error('Mark failed:', error);
            Toast.show({content: t('pages.delivery.orderDetail.markFailed'), position: 'center'});

        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    // 检查跳转来源并返回到相应页面
                    const driverId = searchParams.get('driverId');
                    const date = searchParams.get('date');
                    const tabType = searchParams.get('tabType');
                    const taskTabType = searchParams.get('taskTabType');
                    
                    if (taskTabType) {
                        // 从任务详情页面跳转过来，返回到任务详情页面
                        const taskId = searchParams.get('taskId');
                        // 返回到任务详情页面，并传递tab状态
                        navigate(DspPath.DELIVERY_DSP_DELIVERY_TASK_DETAILS, {
                            state: {
                                taskId: taskId ? parseInt(taskId) : 0,
                                activeTab: taskTabType
                            }
                        });
                    } else if (driverId && tabType) {
                        // 从运营数据页面跳转过来，返回到对应的tab
                        const returnParams = new URLSearchParams();
                        returnParams.set('tabType', tabType);
                        if (date) returnParams.set('date', date);
                        navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS.replace(':driverId', driverId)}?${returnParams.toString()}`);
                    } else {
                        navigate(DspPath.DELIVERY_DSP_OPERATIONAL);
                    }
                }}>
                    {trackingNumber}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                    <div className="mb-1">
                        <InfoRow
                            label={t('pages.dsp.packageDetail.trackingNo')}
                            value={packageDetail.trackingNumber}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.stop')}
                            value={packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '0'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.route')}
                            value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.assignedTime')}
                            value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail?.deliverAt) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.deliveryAttempt')}
                            value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                        />
                        <OrderNoteItem 
                            orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        {(exceptionReason || packageDetail?.exceptionReasonCode) && (
                            <div className="flex justify-between py-2 border-gray-100">
                                <span
                                    className="text-gray-600 text-sm">{`${t('pages.delivery.exception.filedReason')}`}:</span>
                                <span
                                    className={`text-sm font-medium text-right ${exceptionReason ? 'text-violet-600' : 'text-red-600'}`}>
                                    {exceptionReason?.exceptionSubReasons &&
                                    (exceptionSubReasons.length > 0 || (packageDetail?.exceptionSubReasons && packageDetail.exceptionSubReasons.length > 0))
                                        ? (exceptionSubReasons.length > 0
                                            ? exceptionSubReasons.map(s => getLocalizedText({ zh: s.textZh, en: s.textEn, es: s.textEs }, s.textEn)).join(', ')
                                            : packageDetail!.exceptionSubReasons!
                                                .map(code => {
                                                    const sub = exceptionReason!.exceptionSubReasons!.find(s => s.code === code);
                                                    return sub ? getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn) : code;
                                                })
                                                .join(', '))
                                        : exceptionReason
                                            ? getExceptionTypeText(exceptionReason)
                                            : `${packageDetail?.exceptionReasonCode ?? ''} (${t('pages.delivery.exception.exceptionDictionaryNotFound')})`}
                                </span>
                            </div>
                        )}
                        {(exceptionReason || packageDetail?.exceptionReasonCode) && (
                            <>
                                <div className="flex justify-between py-2 border-gray-100">
                                    <span className="text-gray-600 text-sm">{`${t('pages.dsp.operationalDataParticulars.failAt')}`}:</span>
                                    <span className="text-sm font-medium text-right">
                                        {packageDetail?.failAt ? formatTimestampToSecondsTime(packageDetail.failAt) : t('pages.dsp.operationalDataParticulars.notAvailable')}
                                    </span>
                                </div>
                                <div className="flex justify-between py-2 border-gray-100">
                                    <span className="text-gray-600 text-sm">{`${t('pages.dsp.operationalDataParticulars.driver')}`}:</span>
                                    <span className="text-sm font-medium text-right">
                                        {packageDetail?.assignedDriver || t('pages.dsp.operationalDataParticulars.notAvailable')}
                                    </span>
                                </div>
                            </>
                        )}
                        <InfoRow
                            label={t('pages.dsp.packageDetail.taskDate')}
                            value={packageDetail?.taskDate || '-'}
                        />
                    </div>
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                    <div>
                        <InfoRow
                            label={t('pages.dsp.packageDetail.customerName')}
                            value={packageDetail?.recipientName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.phoneNumber')}
                            value={maskPhoneNumber(packageDetail?.recipientPhone) || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.address')}
                            value={packageDetail?.recipientAddress || '-'}
                        />
                    </div>
                </div>

                {canEdit ? (
                    <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                        <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                        {isEditing && (
                            <p className="text-sm text-gray-600 mb-1">{`${t('pages.delivery.exception.photoDesc')}`}</p>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                            <div ref={labelPhotoRef}>
                                <PhotoUploadCard
                                    photoUrl={photos.label?.url}
                                    type="label"
                                    description=""
                                    onTakePhoto={takePhoto}
                                    onDelete={isEditing ? deletePhoto : () => {}}
                                    onView={handleViewPhoto}
                                />
                            </div>
                            <div ref={locationPhotoRef}>
                                <PhotoUploadCard
                                    photoUrl={photos.location?.url}
                                    type="location"
                                    description=""
                                    onTakePhoto={takePhoto}
                                    onDelete={isEditing ? deletePhoto : () => {}}
                                    onView={handleViewPhoto}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                        <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                        <div className="grid grid-cols-2 gap-4">
                            {packageDetail?.exceptionImageS3UrlsJson && packageDetail.exceptionImageS3UrlsJson.length > 0 ? (
                                packageDetail.exceptionImageS3UrlsJson.map((photo: string, index: number) => (
                                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full">
                                        <Image
                                            src={photo}
                                            alt={`${t('pages.dsp.packageDetail.exceptionPhoto')} ${index + 1}`}
                                            fit="cover"
                                            className="w-full h-full object-cover rounded-lg"
                                            onClick={() => setViewingPhoto(photo)}
                                        />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                        <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                    </div>
                                    <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 w-full flex items-center justify-center">
                                        <Image src="/404" className="w-full h-full object-cover rounded-lg"/>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* 查看大图 */}
                <PhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />

                {/* 司机投递备注 */}
                {packageDetail?.driverDeliveryNote?.note && (
                    <div className="bg-white rounded-lg p-4 shadow-sm mx-1 mb-1">
                        <h3 className="text-base font-bold mb-1">{t('pages.dsp.packageDetail.driverRemark')}</h3>
                        <p className="text-sm text-gray-700">
                            {getOrderNoteContent(packageDetail.driverDeliveryNote as any, i18n.language)}
                        </p>
                    </div>
                )}
            </div>

            {canEdit && (
                <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                    {isEditing && exceptionReason ? (
                        <>
                            <Button
                                block
                                color="default"
                                onClick={handleModifyFailProof}
                                disabled={isLoading}
                            >
                                {t('pages.dsp.particulars.modifyFailProof', '修改失败凭证')}
                            </Button>
                            <Button
                                block
                                color="primary"
                                loading={isLoading}
                                disabled={exceptionReason?.isUploadsImages && (!photos.label?.url || !photos.location?.url || isLoading)}
                                onClick={handleCompleteException}
                            >
                                {t('pages.delivery.detail.complete')}
                            </Button>
                        </>
                    ) : (
                        <Button
                            block
                            color="primary"
                            onClick={handleModifyFailProof}
                            disabled={isLoading || dspExceptionDictionaries.length === 0}
                            style={{backgroundColor: '#9333ea', color: 'white'}}
                        >
                            {t('pages.dsp.particulars.modifyFailProof', '修改失败凭证')}
                        </Button>
                    )}
                </div>
            )}

            {/* 异常原因选择抽屉 */}
            <ActionSheet
                visible={exceptionActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className="text-center">{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => {
                    setExceptionActionSheetVisible(false);
                    // 如果关闭抽屉时还没有选择异常原因（且未弹出二级弹窗），返回上一页
                    if (!exceptionReason && !selectedExceptionForSubReasons) {
                        navigate(-1);
                    }
                }}
                actions={dspExceptionDictionaries.map(action => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                    key: action.exceptionReasonCode,
                    onClick: () => handleExceptionActionClick(action)
                }))}
            />

            {/* 地址异常等二级原因弹窗 */}
            <Popup
                visible={selectedExceptionForSubReasons !== null}
                position="bottom"
                onMaskClick={() => {
                    setSelectedExceptionForSubReasons(null);
                    setSelectedSubReasonCodes(new Set());
                }}
                bodyStyle={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
            >
                <div className="p-4 pb-safe">
                    <h3 className="text-center text-gray-800 font-semibold text-base py-3">
                        {selectedExceptionForSubReasons ? t('pages.delivery.orderDetail.wrongAddressReason') : ''}
                    </h3>
                    <div className="max-h-60 overflow-y-auto mb-4 space-y-2">
                        {(selectedExceptionForSubReasons?.exceptionSubReasons ?? []).map(sub => {
                            const checked = selectedSubReasonCodes.has(sub.code);
                            return (
                                <div
                                    key={sub.code}
                                    className={`flex items-center justify-between py-3 px-4 rounded-xl border-2 bg-white active:opacity-90 ${
                                        checked ? 'border-purple-600' : 'border-gray-200'
                                    }`}
                                    onClick={() => {
                                        const multiSelectCodes = ['ERDSP004'];
                                        const isMultiSelect = multiSelectCodes.includes(
                                            selectedExceptionForSubReasons?.exceptionReasonCode ?? ''
                                        );
                                        setSelectedSubReasonCodes(prev => {
                                            if (isMultiSelect) {
                                                const next = new Set(prev);
                                                if (checked) next.delete(sub.code);
                                                else next.add(sub.code);
                                                return next;
                                            }
                                            return prev.has(sub.code) ? new Set() : new Set([sub.code]);
                                        });
                                    }}
                                >
                                    <span className={`text-base ${checked ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                                        {getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn)}
                                    </span>
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            checked ? 'bg-purple-600' : 'border-2 border-gray-300 bg-white'
                                        }`}
                                    >
                                        {checked ? (
                                            <CheckOutline className="text-white" style={{ fontSize: 16 }} />
                                        ) : (
                                            <CheckOutline className="text-gray-300" style={{ fontSize: 16 }} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Button
                        block
                        color="primary"
                        className="w-full"
                        style={{ '--background-color': '#7c3aed', '--border-radius': '8px' } as React.CSSProperties}
                        disabled={selectedSubReasonCodes.size === 0}
                        onClick={() => {
                            if (selectedExceptionForSubReasons) {
                                const subReasons = (selectedExceptionForSubReasons.exceptionSubReasons ?? []).filter(
                                    sub => selectedSubReasonCodes.has(sub.code)
                                );
                                handleSubReasonConfirm(selectedExceptionForSubReasons, subReasons);
                            }
                        }}
                    >
                        {t('pages.delivery.orderDetail.confirm')}
                    </Button>
                </div>
            </Popup>
        </div>
    );
};

export default DspParticularsMarkFailPage;
